// Phase 10 — الفرق الوحيد بين الويب وتطبيق أندرويد.
//
// التطبيق يغلّف تطبيق الويب نفسه: نفس الشاشات ونفس المنطق ونفس الخادم. ما يختلف شيئان
// تقنيّان لا أكثر، وهما محصوران هنا:
//   1. حفظ ملف: المتصفح ينزّله برابط، والـWebView لا يفعل — فيُكتب الملف ثم يُشارَك.
//   2. تعذّر الشبكة: الواجهة محفوظة (عامل الخدمة) فتبقى ظاهرةً بلا إنترنت، والمستخدم يحتاج
//      إخبارًا صريحًا أن العمليات كلها على الخادم.
//
// ومنذ 24/09/2026 التطبيق يفتح الموقع الحيّ (capacitor.config.ts): الكود يأتي من الخادم، والإضافات الأصلية
// من الـAPK المركَّب — فقد يسبق الكودُ الـAPK يومًا، ولذلك يُسأل عن الإضافة قبل استعمالها.
//
// لا قاعدة عمل هنا ولا حالة محلية ولا طابور: كل عملية تبقى على الخادم (قرار المالك D-9).
import { Capacitor } from '@capacitor/core';

/** هل نعمل داخل تطبيق أندرويد؟ المتصفح دائمًا false. */
export const isNativeApp = (): boolean => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

/** Blob إلى base64 بلا البادئة — ما تطلبه Filesystem.writeFile. */
function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('تعذّرت قراءة الملف'));
    r.onload = () => {
      const s = String(r.result ?? '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.readAsDataURL(blob);
  });
}

/**
 * حفظ ملف داخل تطبيق أندرويد.
 *
 * الـWebView لا ينفّذ `<a download>` ولا روابط blob، فيُكتب الملف في مساحة التطبيق الخاصة
 * ثم تُفتح ورقة المشاركة ليحفظه المستخدم حيث يشاء أو يرسله. المحتوى هو ما أرسله الخادم
 * حرفيًا — لا يُعاد توليده ولا يُقرأ ولا يُعدَّل هنا.
 *
 * الإضافتان تُحمَّلان عند الحاجة فقط، فلا يحمّلهما المتصفح إطلاقًا.
 */
export async function saveFileNative(blob: Blob, fileName: string): Promise<void> {
  // تطبيقٌ قديم على الهاتف لا يحمل الإضافة: رسالةٌ مفهومة بدل «not implemented»
  if (!Capacitor.isPluginAvailable('Filesystem') || !Capacitor.isPluginAvailable('Share')) {
    throw new Error('حفظ الملفات يحتاج تحديث تطبيق الموبايل — اطلب النسخة الجديدة من الإدارة');
  }
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ]);
  // Cache: مساحة خاصة بالتطبيق، وليست تخزينًا عامًا — والنظام ينظّفها عند الحاجة
  const written = await Filesystem.writeFile({
    path: fileName,
    data: await toBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  });
  await Share.share({ title: fileName, url: written.uri });
}

/** حالة الاتصال كما يراها النظام. الـWebView يدعمها كما يدعمها المتصفح. */
export const isOnline = (): boolean => {
  try { return typeof navigator === 'undefined' || navigator.onLine !== false; } catch { return true; }
};
